import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDatabase, faCheckCircle, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNotification } from "../../context/NotificationContext";
import {
    getCustomerFormFields,
    getTicketFormFields,
    findCustomerInfo,
    submitTicket,
} from "../../api/ticketsApi";
import { getFormFieldApiSettings } from "../../api/settingApi";
import { callExternalApi } from "../../api/externalApi";
import { isEmpty } from "../util/utils";
import { sessionVariables } from "../util/constants";
import LoadingPage from "../util/LoadingPage";
import { HttpStatusCode } from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const NewTickets = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const notify = useNotification();

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [customerId, setCustomerId] = useState(0);

    const [dynamicForm, setDynamicForm] = useState(null);
    const [ticketDynamicForm, setTicketDynamicForm] = useState([]);
    const [ticketFormId, setTicketFormId] = useState(0);

    const [customerFormValues, setCustomerFormValues] = useState({});
    const [ticketFormValues, setTicketFormValues] = useState({});
    const [formValues, setFormValues] = useState({
        dueDate: tomorrow.toISOString().split("T")[0],
    });

    const [paramInputValues, setParamInputValues] = useState({});
    const [apiData, setApiData] = useState([]);
    const [loadingApiField, setLoadingApiField] = useState(null);

    const [fieldErrors, setFieldErrors] = useState({}); // Track validation errors

    useEffect(() => {
        fetchFormFields();
    }, []);

    const fetchFormFields = async () => {
        setLoading(true);
        try {
            const response = await getCustomerFormFields();
            setDynamicForm(response);
        } catch {
            notify("error", "Failed to load form fields");
        } finally {
            setLoading(false);
        }
    };

    const fetchTicketFormFields = async (type) => {
        setLoading(true);
        try {
            const response = await getTicketFormFields(type);
            setTicketDynamicForm(response);
        } catch {
            setTicketDynamicForm([]);
            notify("error", "Failed to load ticket form fields");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (searchQuery.length < 3) return;
        setLoading(true);
        try {
            const response = await findCustomerInfo(searchQuery);
            setSearchResults(response);
        } catch {
            notify("error", "Search failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectCustomer = (id) => {
        const selected = searchResults.find((r) => r.id === id);
        if (selected) {
            setCustomerFormValues(selected.jsonData);
            setCustomerId(selected.id);
            setStep(2);
        }
    };

    const handleFormInputChange = (e, formType) => {
        const { name, value, type, checked } = e.target;
        const updatedValue = type === "checkbox" ? checked : value;

        if (formType === "customer") {
            setCustomerFormValues((prev) => ({ ...prev, [name]: updatedValue }));
            if (fieldErrors[name]) {
                setFieldErrors((prev) => ({ ...prev, [name]: false }));
            }
        } else if (formType === "ticket") {
            setTicketFormValues((prev) => ({ ...prev, [name]: updatedValue }));
            if (fieldErrors[name]) {
                setFieldErrors((prev) => ({ ...prev, [name]: false }));
            }
        }

        const field = formType === "customer"
            ? dynamicForm?.customerForm?.find((f) => f.label === name)
            : ticketDynamicForm?.find((f) => f.label === name);

        if (field?.type === "text" && (field.required || field.hasApi)) {
            setParamInputValues((prev) => ({ ...prev, [name]: value }));
        }
    };

    const loadFieldApiSettings = async (dyFormId, fieldName) => {
        try {
            let apiSettingsArray = JSON.parse(sessionStorage.getItem(sessionVariables.sv_DynamicFormApiSetting)) || [];
            if (!Array.isArray(apiSettingsArray)) apiSettingsArray = [apiSettingsArray];

            const exists = apiSettingsArray.some(
                (item) => String(item.dyFormId) === String(dyFormId) && String(item.inputLabel) === String(fieldName)
            );
            if (exists) return;

            const response = await getFormFieldApiSettings(dyFormId, fieldName);
            if (response.status === 200 && response.data) {
                apiSettingsArray.push(response.data);
                sessionStorage.setItem(sessionVariables.sv_DynamicFormApiSetting, JSON.stringify(apiSettingsArray));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const loadExternalApiData = async (fieldName, dyFormId, inputValue) => {
        if (!inputValue?.trim()) {
            notify("error", "Input value is empty");
            return;
        }
        setApiData([]);
        setLoadingApiField(fieldName);

        try {
            const orgSetting = JSON.parse(sessionStorage.getItem(sessionVariables.sv_OrgApiSetting));
            if (!orgSetting) return notify("error", "Organization API settings not found");

            const { apiAddress, apiKey, password: apiPass, username: apiUser, useApiKey } = orgSetting;

            const storedData = sessionStorage.getItem(sessionVariables.sv_DynamicFormApiSetting);
            if (!storedData) return notify("error", "Dynamic API settings not found");

            const apiSettingsArray = Array.isArray(JSON.parse(storedData)) ? JSON.parse(storedData) : [JSON.parse(storedData)];

            const fieldConfig = apiSettingsArray.find(
                (item) => String(item.dyFormId) === String(dyFormId) && String(item.inputLabel) === String(fieldName)
            );

            if (!fieldConfig) return notify("error", `No API configuration found for "${fieldName}"`);

            let apiUrl = `${apiAddress}${fieldConfig.endpoint}`;
            let requestBody = null;

            if (fieldConfig.methodType === "GET") {
                const params = new URLSearchParams();
                for (const [key, value] of Object.entries(paramInputValues)) {
                    if (value?.trim()) params.append(key, value);
                }
                params.append(fieldConfig.inputLabel, inputValue);
                params.append("$rowCount", fieldConfig.rowCount || 10);
                if (useApiKey) params.append("apiKey", apiKey);
                else {
                    params.append("username", apiUser);
                    params.append("password", apiPass);
                }
                apiUrl += `?${params.toString()}`;
            } else {
                requestBody = {
                    ...paramInputValues,
                    [fieldConfig.inputLabel]: inputValue,
                    ...(useApiKey ? { apiKey } : { username: apiUser, password: apiPass }),
                };
            }

            const res = await callExternalApi(apiUrl, fieldConfig.methodType, requestBody);
            if (res?.status === 200 && Object.keys(res.data.data).length > 0) {
                const newData = res.data.data;
                if (dynamicForm?.customerForm?.some((f) => f.label === fieldName)) {
                    setCustomerFormValues((prev) => ({ ...prev, ...newData }));
                }
                if (ticketDynamicForm?.some((f) => f.label === fieldName)) {
                    setTicketFormValues((prev) => ({ ...prev, ...newData }));
                }
            } else {
                toast.info("No data found for this input");
            }
        } catch (error) {
            toast.error(`Failed to load data: ${error.message || "Unexpected error"}`);
        } finally {
            setLoadingApiField(null);
        }
    };

    const formatLabel = (label) =>
        label.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim();

    const renderFormFields = (fields, dyFormId, formType) => {
        if (!fields) return null;

        return fields.map((field) => {
            if (field.type === "text" && field.hasApi) loadFieldApiSettings(dyFormId, field.label);

            const value = formType === "customer"
                ? customerFormValues[field.label] || ""
                : ticketFormValues[field.label] || "";

            const isError = fieldErrors[field.label];
            const isValid = !isError && value?.toString().trim() && field.required;

            const commonProps = {
                id: field.label,
                name: field.label,
                placeholder: field.placeholder || "",
                onChange: (e) => handleFormInputChange(e, formType),
                className: `mt-1 block w-full px-4 py-2 border rounded-lg
              ${isError ? "border-red-500 focus:ring-red-300" : "border-gray-300 focus:ring-teal-500"}
              focus:outline-none transition-colors duration-200 pr-10`, // space for icon
                value,
            };

            const formattedLabel = field.required ? `${formatLabel(field.label)} *` : formatLabel(field.label);

            return (
                <div key={field.label} className="bg-white p-2 rounded-lg mb-2 relative">
                    <label className={`block text-sm font-medium ${isError ? "text-red-600" : "text-blue-800"}`}>
                        {formattedLabel}
                    </label>

                    {field.type === "textarea" ? (
                        <textarea {...commonProps} rows={field.rows || 5} />
                    ) : field.type === "dropdown" ? (
                        <select {...commonProps}>
                            <option value="" disabled>Select an option</option>
                            {field.options?.map((opt, i) => (
                                <option key={i} value={opt}>{opt}</option>
                            ))}
                        </select>
                    ) : field.type === "checkbox" ? (
                        <div className="flex items-center gap-2 mt-1">
                            <input {...commonProps} type="checkbox" checked={value || false} />
                            <span>{formattedLabel}</span>
                            {isValid && (
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 ml-2" />
                            )}
                        </div>
                    ) : (
                        <div className="relative">
                            <input {...commonProps} type={field.type || "text"} />
                            {field.hasApi && (
                                <FontAwesomeIcon
                                    icon={faDatabase}
                                    className={`absolute right-8 top-1/2 -translate-y-1/2 cursor-pointer ${loadingApiField === field.label ? "text-teal-500 animate-spin" : "text-gray-500"}`}
                                    title={loadingApiField === field.label ? "Loading..." : "Fetch data"}
                                    onClick={() => loadExternalApiData(field.label, dyFormId, value)}
                                />
                            )}
                            {isValid && (
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500"
                                />
                            )}
                        </div>
                    )}

                    {isError && <p className="mt-1 text-sm text-red-600">This field is required</p>}
                    {field.helperText && !isError && <p className="mt-1 text-sm text-gray-500">{field.helperText}</p>}
                </div>
            );
        });
    };

    const validateFormFields = (fields, values) => {
        const errors = {};
        fields?.forEach((f) => {
            if (f.required && !values[f.label]?.toString().trim()) errors[f.label] = true;
        });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNextStep = () => {
        if (validateFormFields(dynamicForm?.customerForm, customerFormValues)) {
            setStep(3);
        } else {
            toast.error("Please fill all required fields");
        }
    };

    const handleSubmit = async () => {
        const ticketValid = validateFormFields(ticketDynamicForm, ticketFormValues);
        if (!ticketValid) {
            toast.error("Please fill all required ticket fields");
            return;
        }

        const requiredSelects = ["problemHead", "problemCategory", "priority", "progress"];
        const missingSelects = requiredSelects.filter((key) => !formValues[key]);
        if (missingSelects.length > 0) {
            toast.error("Please select all ticket options!");
            return;
        }

        const customerValid = validateFormFields(dynamicForm?.customerForm, customerFormValues);
        if (!customerValid) {
            toast.error("Customer information is incomplete!");
            setStep(2);
            return;
        }
        setLoading(true);
        try {
            const payload = { customerId, customerFormValues, ticketFormValues, formValues };
            const res = await submitTicket(payload);
            if (res.status === HttpStatusCode.Created) {

                toast.success(res.data);
                setCustomerFormValues({});
                setTicketFormValues({});
                setFormValues({ dueDate: tomorrow.toISOString().split("T")[0] });
                setStep(1);
                setFieldErrors({});
                setCustomerId(0);
                setSearchQuery("");
                setSearchResults([]);
            }
        } catch {
            toast.error("Failed to submit the ticket");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingPage />;
    if (!dynamicForm) return <div className="text-center text-red-500">Failed to load form fields</div>;

    const { priorityList, progressList, problemHeadList, problemCategoryList, customerFormId, customerForm } = dynamicForm;

    return (
        <div className="max-w-3xl mx-auto p-6">
            <ToastContainer position="top-right" />
            <h2 className="text-xl font-bold mb-6 text-teal-600 border-b border-teal-700 pb-2 bg-[#EFF6FF] sticky top-0 z-10">
                New Ticket
            </h2>

            {step === 1 && (
                <div>
                    {/* Search Input */}
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyUp={(e) => e.key === "Enter" && handleSearch()}
                        placeholder="Search customer..."
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-lg"
                    />

                    {/* Search Results */}
                    {searchResults.length > 0 ? (
                    <ul className="mt-4 space-y-4">
                        {searchResults.map((r) => {
                        const isSelected = r.id === customerId;
                        return (
                            <li
                            key={r.id}
                            className={`p-4 border rounded-lg shadow-sm cursor-pointer transition-all duration-300
                                ${isSelected
                                ? "bg-[#A71D31] text-white scale-105"
                                : "bg-white text-gray-800 hover:bg-[#A71D31] hover:text-white hover:scale-110"
                                }`}
                            onClick={() => handleSelectCustomer(r.id)}
                            >
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(r.jsonData).map(([key, value]) => (
                                <div key={key} className="flex flex-col">
                                    <span className="text-sm text-gray-500">{key}</span>
                                    <span className="font-medium">{String(value)}</span>
                                </div>
                                ))}
                            </div>
                            </li>
                        );
                        })}
                    </ul>
                    ) : (
                    searchQuery.trim().length >= 3 && (
                        <div
                        className="mt-6 p-4 border rounded-lg shadow-sm bg-white hover:bg-[#A71D31] hover:text-white transition-all duration-300 cursor-pointer"
                        onClick={() => {
                            // Prefill all text and textarea fields with search query
                            const prefilledValues = {};
                            dynamicForm?.customerForm?.forEach((field) => {
                                if (["text", "textarea"].includes(field.type)) {
                                    prefilledValues[field.label] = searchQuery;
                                }
                            });
                            setCustomerFormValues(prefilledValues);
                            setCustomerId(0);
                            setStep(2);
                        }}
                        >
                        <div className="flex flex-col items-start">
                            <span className="text-gray-700 text-base font-medium">
                            No customer found for ‘{searchQuery}’
                            </span>
                            <span className="text-sm text-gray-500 mt-1">
                            Click to create a new entry with this information
                            </span>
                        </div>
                        </div>
                    )
                    )}
                </div>
                )
            }


            {step === 2 && (
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-3xl mx-auto transition-transform duration-300 hover:scale-102">
                    <h3 className="text-2xl font-bold mb-6 text-teal-600 border-b pb-3">
                        Customer Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {renderFormFields(customerForm, customerFormId, "customer")}
                    </div>

                    <div className="flex justify-between mt-8">
                        <button
                            className="px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                            onClick={() => setStep(1)}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} /> Back
                        </button>

                        <button
                            className="px-6 py-3 bg-[#41EAD4] text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400"
                            onClick={handleNextStep}
                        >
                            Next: Ticket Info <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div>
                    <h3 className="text-lg font-semibold mb-4 text-teal-700">Customer Summary</h3>
                    <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(customerFormValues).map(([key, value]) => (
                                <div key={key} className="flex flex-col">
                                    <span className="text-sm text-gray-500">{formatLabel(key)}</span>
                                    <span className="font-medium text-gray-800">{String(value)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Ticket Info</h3>
                    <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label>Ticket Head</label>
                                <select
                                    value={formValues.problemHead || ""}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        setFormValues((prev) => ({ ...prev, problemHead: val }));
                                        setTicketFormId(val);
                                        fetchTicketFormFields(val);
                                    }}
                                    className="w-full px-3 py-2 border rounded"
                                >
                                    <option value="" disabled>Select Ticket Head</option>
                                    {problemHeadList.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>Problem Category</label>
                                <select
                                    value={formValues.problemCategory || ""}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, problemCategory: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded"
                                >
                                    <option value="" disabled>Select Category</option>
                                    {problemCategoryList.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>Priority</label>
                                <select
                                    value={formValues.priority || ""}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, priority: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded"
                                >
                                    <option value="" disabled>Select Priority</option>
                                    {priorityList.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>Progress Status</label>
                                <select
                                    value={formValues.progress || ""}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, progress: Number(e.target.value) }))}
                                    className="w-full px-3 py-2 border rounded"
                                >
                                    <option value="" disabled>Select Status</option>
                                    {progressList.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mt-6">
                                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
                                    Due Date
                                </label>
                                <input
                                    type="date"
                                    id="dueDate"
                                    name="dueDate"
                                    value={formValues.dueDate || new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split("T")[0]}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, dueDate: e.target.value }))}
                                    min={new Date().toISOString().split("T")[0]}
                                    className="block w-full px-4 py-2 mt-1 border rounded-md focus:ring-teal-500 focus:border-teal-500"
                                />
                            </div>

                        </div>

                        {renderFormFields(ticketDynamicForm, ticketFormId, "ticket")}

                        <div className="flex justify-end mt-4 gap-2">
                            <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setStep(2)}>Back</button>
                            <button className="px-4 py-2 bg-teal-500 text-white rounded" onClick={handleSubmit}>
                                Submit Ticket
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewTickets;
